// Hooks: notifications (inbox, push devices, personal Telegram), global search, project channels.
import { useEffect } from "react";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BASE, del, get, patch, post } from "./client";
import type { ChannelView } from "./types/ChannelView";
import type { CreateChannelRequest } from "./types/CreateChannelRequest";
import type { DeviceView } from "./types/DeviceView";
import type { NotificationDetail } from "./types/NotificationDetail";
import type { NotificationList } from "./types/NotificationList";
import type { NotificationPing } from "./types/NotificationPing";
import type { PushConfig } from "./types/PushConfig";
import type { RegisterDeviceRequest } from "./types/RegisterDeviceRequest";
import type { TelegramLink } from "./types/TelegramLink";
import type { TelegramStatus } from "./types/TelegramStatus";
import type { SearchResult } from "./types/SearchResult";
import type { UpdateChannelRequest } from "./types/UpdateChannelRequest";

const enc = encodeURIComponent;
export const NOTIFICATIONS_KEY = ["notifications"] as const;

// ───────────── Notifications ─────────────

/** Latest notifications for the bell (and the unread count). */
export function useNotifications(limit = 20) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, "latest", limit],
    queryFn: () => get<NotificationList>(`/notifications?limit=${limit}`),
    staleTime: 60_000,
  });
}

const PAGE = 30;

/** The full notifications page, paged with `before`. */
export function useNotificationPages(unreadOnly: boolean) {
  return useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_KEY, "pages", unreadOnly],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: String(PAGE) });
      if (unreadOnly) p.set("unread", "true");
      if (pageParam) p.set("before", pageParam);
      return get<NotificationList>(`/notifications?${p}`);
    },
    getNextPageParam: (last) => (last.items.length < PAGE ? null : (last.items[last.items.length - 1]?.id ?? null)),
  });
}

/** One notification with its Telegram / push delivery. */
export function useNotification(id: string) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, "one", id],
    queryFn: () => get<NotificationDetail>(`/notifications/${enc(id)}`),
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<NotificationPing>(`/notifications/${enc(id)}/read`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<NotificationPing>("/notifications/read-all"),
    onSuccess: () => void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

/** Refetches notifications whenever the server pings this user's stream. */
export function useNotificationStream() {
  const qc = useQueryClient();
  useEffect(() => {
    if (typeof EventSource === "undefined") return;
    const source = new EventSource(`${API_BASE}/notifications/stream`, { withCredentials: true });
    const refresh = () => void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    source.addEventListener("notification", refresh);
    source.addEventListener("resync", refresh);
    return () => {
      source.removeEventListener("notification", refresh);
      source.removeEventListener("resync", refresh);
      source.close();
    };
  }, [qc]);
}

// ───────────── Push devices ─────────────

export const PUSH_CONFIG_KEY = ["push", "config"] as const;
export const DEVICES_KEY = ["push", "devices"] as const;

export function usePushConfig() {
  return useQuery({ queryKey: PUSH_CONFIG_KEY, queryFn: () => get<PushConfig>("/push/config"), staleTime: Infinity });
}

export function useDevices(enabled: boolean) {
  return useQuery({ queryKey: DEVICES_KEY, queryFn: () => get<DeviceView[]>("/devices"), enabled });
}

export function useRegisterDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RegisterDeviceRequest) => post<DeviceView>("/devices", body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DEVICES_KEY }),
  });
}

export function useUnregisterDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => del(`/devices?token=${enc(token)}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DEVICES_KEY }),
  });
}

// ───────────── Personal Telegram ─────────────

const TELEGRAM_KEY = ["me", "telegram"] as const;

/** `waiting`: re-check every few seconds while the person is pressing Start in Telegram. */
export function useTelegramStatus(waiting = false) {
  return useQuery({
    queryKey: TELEGRAM_KEY,
    queryFn: () => get<TelegramStatus>("/me/telegram"),
    refetchInterval: waiting ? 3000 : false,
  });
}

export function useTelegramLink() {
  return useMutation({ mutationFn: () => post<TelegramLink>("/me/telegram/link") });
}

export function useTelegramDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => del("/me/telegram"),
    onSuccess: () => void qc.invalidateQueries({ queryKey: TELEGRAM_KEY }),
  });
}

// ───────────── Search ─────────────

export function useSearch(q: string) {
  const term = q.trim();
  return useQuery({
    queryKey: ["search", term],
    queryFn: () => get<SearchResult[]>(`/search?q=${enc(term)}`),
    enabled: term.length >= 2,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// ───────────── Channels ─────────────

const channelsPath = (projectKey: string) => `/projects/${enc(projectKey)}/channels`;
const channelsKey = (projectKey: string) => ["channels", projectKey] as const;

export function useChannels(projectKey: string, enabled = true) {
  return useQuery({
    queryKey: channelsKey(projectKey),
    queryFn: () => get<ChannelView[]>(channelsPath(projectKey)),
    enabled,
  });
}

function useChannelMutation<V>(projectKey: string, fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: channelsKey(projectKey) }),
  });
}

export const useCreateChannel = (projectKey: string) =>
  useChannelMutation(projectKey, (body: CreateChannelRequest) => post<ChannelView>(channelsPath(projectKey), body));

export const useUpdateChannel = (projectKey: string) =>
  useChannelMutation(projectKey, ({ id, body }: { id: string; body: Partial<UpdateChannelRequest> }) =>
    patch<ChannelView>(`${channelsPath(projectKey)}/${enc(id)}`, body),
  );

export const useDeleteChannel = (projectKey: string) =>
  useChannelMutation(projectKey, (id: string) => del(`${channelsPath(projectKey)}/${enc(id)}`));

export const useTestChannel = (projectKey: string) =>
  useChannelMutation(projectKey, (id: string) => post<ChannelView>(`${channelsPath(projectKey)}/${enc(id)}/test`));
